import { Link as ReachLink } from '@reach/router'
import React, { useEffect, ForwardedRef } from 'react'
import { useMergeRefs } from '@chakra-ui/icons'
import { motion } from 'framer-motion'
import { asLink } from '../links/Links'
import { ChevronLeft, ChevronRight } from '~/icons'
import {
  forwardRef,
  RadioGroup,
  RadioGroupProps,
  Tab,
  TabProps,
  TabList,
  TabListProps,
  Tabs,
  TabsProps,
  RadioProps,
  chakra
} from '../../chakra'
import { CustomRadio } from '../form_controls/FormControls'
import { useMsg } from '~/common/localizationUtils'
import { useHasChanged } from '~/hooks/usePrevious'

type TabScrollArrowButtonProps = {
  direction: 'left' | 'right'
  onClick: () => void
}

const ARROW_BTN_WIDTH = 32
const MotionButton = motion(chakra.button)
const TabScrollArrowButton = forwardRef<TabScrollArrowButtonProps, 'button'>(({ direction, ...props }, ref) => {
  const msg = useMsg()
  return (
    <MotionButton
      ref={ref}
      aria-label={direction === 'left' ? msg('common.ui.scroll_left') : msg('common.ui.scroll_right')}
      aria-hidden="true"
      tabIndex={-1}
      key={`${direction}-arrow`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: 'linear' }}
      sx={{
        zIndex: 2,
        position: 'absolute',
        top: 0,
        [direction]: 0,
        height: '100%',
        background: 'white',
        border: '1px solid var(--vndly-color-container-border)',
        width: `${ARROW_BTN_WIDTH}px`
      }}
      {...props}>
      {direction === 'left' ? <ChevronLeft /> : <ChevronRight />}
    </MotionButton>
  )
})

const useScrollableTabList = (props: ScrollableTabListProps, ref: ForwardedRef<HTMLElement>) => {
  const innerRef = React.useRef<HTMLDivElement>(null)
  const tabRefs = React.useRef<(HTMLDivElement | null)[]>([])
  const [showLeftArrowBtn, setShowLeftArrowBtn] = React.useState(false)
  const [showRightArrowBtn, setShowRightArrowBtn] = React.useState(false)

  const BTN_BORDER_WIDTH = 2
  const OFFSET = 40
  const scrollInDirection = (direction: 'left' | 'right') => {
    if (!innerRef.current || !tabRefs.current.length) return

    const currentScrollLeft = innerRef.current.scrollLeft
    const clientWidth = innerRef.current.clientWidth - ARROW_BTN_WIDTH
    const VISIBLE_PORTION = 2

    if (direction === 'right') {
      // Find the first tab to the right that is a quarter or less visible and scroll it so that it's right edge is at the right edge of the container
      for (let i = 0; i < tabRefs.current.length; i++) {
        const tab = tabRefs.current[i]
        if (tab) {
          const isLastTab = i === tabRefs.current.length - 1
          const isLessThanAQuarterVisible =
            tab.offsetLeft + tab.offsetWidth / VISIBLE_PORTION > currentScrollLeft + clientWidth
          if (isLastTab || isLessThanAQuarterVisible) {
            const newScrollLeft = Math.ceil(tab.offsetLeft + tab.offsetWidth - clientWidth + BTN_BORDER_WIDTH + OFFSET)
            const lastTab = tabRefs.current[tabRefs.current.length - 1]
            // If more than a quarter of the last tab is visible, just scroll to the end, don't make the user click the arrow
            // again just to make it scroll a tiny bit more.
            const shouldWeJustScrollToTheEnd =
              lastTab && lastTab.offsetLeft + lastTab.offsetWidth / 4 <= newScrollLeft + clientWidth
            innerRef.current.scrollTo({
              left: shouldWeJustScrollToTheEnd ? innerRef.current.scrollWidth : newScrollLeft,
              behavior: 'smooth'
            })
            break
          }
        }
      }
    } else {
      for (let i = tabRefs.current.length - 1; i >= 0; i--) {
        const tab = tabRefs.current[i]
        if (tab) {
          const isFirstTab = i === 0
          const isLessThanAQuarterVisible = tab.offsetLeft + tab.offsetWidth / VISIBLE_PORTION < currentScrollLeft
          if (isFirstTab || isLessThanAQuarterVisible) {
            const newScrollLeft = Math.ceil(tab.offsetLeft - ARROW_BTN_WIDTH - BTN_BORDER_WIDTH - OFFSET)
            const firstTab = tabRefs.current[0]
            // If more than a quarter of the first tab is visible, just scroll to the start, don't make the user click the arrow
            // again just to make it scroll a tiny bit more.
            const shouldWeJustScrollToTheStart =
              firstTab && firstTab.offsetLeft + firstTab.offsetWidth / 4 >= newScrollLeft
            innerRef.current.scrollTo({
              left: shouldWeJustScrollToTheStart ? 0 : newScrollLeft,
              behavior: 'smooth'
            })
            break
          }
        }
      }
    }
  }

  const scrollToTabIndex = (index: number) => {
    if (!innerRef.current || !tabRefs.current.length) return
    const tab = tabRefs.current[index]
    if (tab) {
      innerRef.current.scrollTo({
        left: Math.ceil(tab.offsetLeft - ARROW_BTN_WIDTH - BTN_BORDER_WIDTH - OFFSET),
        behavior: 'smooth'
      })
    }
  }

  const leftArrowButton = showLeftArrowBtn ? (
    <TabScrollArrowButton direction="left" onClick={() => scrollInDirection('left')} />
  ) : null
  const rightArrowButton = showRightArrowBtn ? (
    <TabScrollArrowButton direction="right" onClick={() => scrollInDirection('right')} />
  ) : null

  const mergedRef = useMergeRefs(ref, innerRef)

  const validChildren = React.Children.toArray(props.children).filter(Boolean)
  if (useHasChanged(validChildren.length)) {
    tabRefs.current = []
  }
  const children = validChildren.map((child, index) =>
    React.cloneElement(child as React.ReactElement, {
      ref: (el: HTMLDivElement) => {
        tabRefs.current[index] = el
      }
    })
  )

  React.useEffect(() => {
    const handleResize = () => {
      if (innerRef.current) {
        const hasMoreContentRight =
          Math.ceil(innerRef.current.scrollLeft) < innerRef.current.scrollWidth - innerRef.current.clientWidth
        const hasMoreContentLeft = innerRef.current.scrollLeft > 0
        setShowLeftArrowBtn(hasMoreContentLeft)
        setShowRightArrowBtn(hasMoreContentRight)
      }
    }
    handleResize()

    window.addEventListener('resize', handleResize)
    innerRef.current?.addEventListener('scroll', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      innerRef.current?.removeEventListener('scroll', handleResize)
    }
  }, [validChildren.length])

  useEffect(() => {
    const onFocus = (index: number) => () => {
      scrollToTabIndex(index)
    }

    tabRefs.current.forEach((tab, index) => {
      tab?.addEventListener?.('focus', onFocus(index))
    })

    return () => {
      tabRefs.current.forEach((tab, index) => {
        tab?.removeEventListener?.('focus', onFocus(index))
      })
    }
  }, [validChildren.length])

  return {
    leftArrowButton,
    rightArrowButton,
    getTabListProps: (): TabListProps => ({
      ...props,
      sx: {
        overflowX: 'auto',
        padding: '2px 2px 0',
        scrollBarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        },

        borderBottom: props.hideUnderline ? 'none' : '1px solid',
        '[data-page-header] &': {
          borderBottom: 'none' // Always hide the bottom border when this component is used in a PageHeader
        },
        ...props.sx
      },
      ref: mergedRef,
      children
    })
  }
}

type ScrollableTabListProps = {
  hideUnderline?: boolean
} & TabListProps

export const ScrollableTabList = forwardRef<ScrollableTabListProps, 'div'>((props, ref) => {
  const { leftArrowButton, rightArrowButton, getTabListProps } = useScrollableTabList(props, ref)

  return (
    <>
      {leftArrowButton}
      <TabList data-testid="tab-list" {...getTabListProps()} />
      {rightArrowButton}
    </>
  )
})

/**
 * Nav Tabs
 *
 * Used to navigate easily between views with different url paths within a page. This is the wrapper container for Nav tabs.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const NavTabs = forwardRef(({ children, ...props }: TabsProps, ref) => (
  <Tabs
    ref={ref}
    sx={{
      position: 'relative',
      '[data-page-header] &': {
        mb: '-1px'
      },
      ...props.sx
    }}
    {...props}>
    {children}
  </Tabs>
))

NavTabs.displayName = 'NavTabs'

/**
 * Nav Tab List
 *
 * This component will be the wrapper to form a Nav tab list. It will automatically add an overflow menu when there are more than
 * five tabs.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const NavTabList = forwardRef<ScrollableTabListProps, 'div'>((props, ref) => {
  return <ScrollableTabList ref={ref} hideUnderline {...props} />
})

NavTabList.displayName = 'NavTabList'

type NavTabProps = {
  href: any
} & TabProps

/**
 * Nav Tab
 *
 * Individual nav tab component. NavTab elements need to be wrapped inside the NavTabs. This component is a tab link
 * supporting NextJS links.
 *
 * Based on Chakra's Tab component.
 *
 * TODO: Move ToggledNextLink to the DS
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const NavTab = forwardRef<NavTabProps, 'button'>((props, ref) => (
  /* @ts-ignore */
  <Tab {...asLink(props.href)} ref={ref} px={4} py={3} whiteSpace="nowrap" variant="tabLink" {...props} />
))

NavTab.displayName = 'NavTab'

/**
 * Nav Tab Reach
 *
 * Individual nav tab component. NavTabReach elements need to be wrapped inside the NavTabs. This component is a tab link
 * supporting reach router links.
 *
 * Based on Chakra's Tab component.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const NavTabReach = forwardRef<NavTabProps, 'button'>(({ href, ...props }, ref) => (
  <Tab as={ReachLink} to={href} px={4} py={3} whiteSpace="nowrap" variant="tabLink" ref={ref} {...props} />
))

NavTabReach.displayName = 'NavTabReach'

/**
 * Page Tabs
 *
 * This component is the parent wrapper of a series of PageTab components. Page tabs would be used when
 * tab that don't need to change url paths and stay within the same context is needed.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const PageTabs = forwardRef(({ children, ...props }: TabsProps, ref) => (
  <Tabs ref={ref} position="relative" {...props}>
    {children}
  </Tabs>
))

PageTabs.displayName = 'PageTabs'

/**
 * Page Tabs List
 *
 * This component will be the wrapper to form a page tab list. It will automatically add an overflow menu when there are more than
 * five tabs.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const PageTabList = forwardRef<ScrollableTabListProps, 'div'>((props, ref) => (
  <NavTabList ref={ref} hideUnderline={false} {...props} />
))

PageTabList.displayName = 'PageTabList'

/**
 * Page Tab
 *
 * Individual page tab component. Used to set each page tab as a button.
 *
 * Based on Chakra's Tab component
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const PageTab = forwardRef(({ children, ...props }: TabProps, ref) => (
  /* @ts-ignore */
  <Tab ref={ref} px={4} py={3} whiteSpace="nowrap" {...props}>
    {children}
  </Tab>
))

PageTab.displayName = 'PageTab'

/**
 * Filter Tabs
 *
 * This component is the parent wrapper of a series of FilterTab components. Filter tabs would be used when
 * prioritizing the same group of data. Used up to four tabs in a group.
 *
 * Just Chakra's Tabs component with a custom "button" variant and isManual set to true.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-filter-tabs--docs
 */
export const FilterTabs = forwardRef<RadioGroupProps, 'div'>((props, ref) => {
  const msg = useMsg()
  return (
    <RadioGroup
      variant="unstyled"
      ref={ref}
      display="flex"
      gap={2}
      aria-label={msg('common.action_bar.filter_tabs.group')}
      {...props}
    />
  )
})

FilterTabs.displayName = 'FilterTabs'

/**
 * Filter Tab
 *
 * Use up to four filter tabs.  Typically, you will want to include an “All” option.
 *
 * If possible, it is highly recommended to include counts on each item, as these can replace result counts in a list view.
 *
 * FilterTab elements need to be wrapped inside the FilterTabs
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-filter-tabs--docs
 */
export const FilterTab = forwardRef<RadioProps, 'input'>((props, ref) => {
  return <CustomRadio ref={ref} variant="filterTab" {...props} />
})

FilterTab.displayName = 'FilterTab'
